import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from "@enkor/react-query";
import { formatDate, ignoreTimezone } from "@enkor/date-fns";

import BaseApi from "@logics/api/baseApi";
import {
  ResponseProject,
  ResponseProjectComment,
  ResponseProjectDetail,
  ResponseProjectEvent,
  ResponseUserProject,
} from "@models/data-contracts";
import {
  ApiResponseDto,
  ProjectListSearchParamType,
  ProjectScheduleEventType,
  ProjectStaticEventType,
} from "@models/client";
import { groupByList } from "@logics/utils/arrayHandler";

import { projectQueryKeys } from "../constants/projectQueryKeys";
import ProjectEventInfoViewModel from "../viewModel/ProjectEventInfoViewModel";
import ProjectViewModel from "../viewModel/ProjectViewModel";
import ProjectCommentViewModel from "../views/viewModel/ProjectCommentViewModel";
// import EventViewModel from "../viewModel/EventViewModel";
// import CommentViewModel from "../viewModel/CommentViewModel";
// import AttendedUserViewModel from "../viewModel/AttendedUserViewModel";

const userApi = new BaseApi("/user");
const projectApi = new BaseApi("/project");

// ------------------------------------------------------------------------

export const useFetchProjectListQuery = (
  options?: UseQueryOptions<
    ApiResponseDto<ResponseProject[]>,
    unknown,
    ResponseProject[]
  >,
) => {
  return useQuery<
    ApiResponseDto<ResponseProject[]>,
    unknown,
    ResponseProject[]
  >({
    queryKey: projectQueryKeys.getProjectList(),
    queryFn: () => projectApi.get(""),
    select: ({ data }) => data,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchMyProjectListQuery = (
  options?: UseQueryOptions<
    ApiResponseDto<ResponseUserProject[]>,
    unknown,
    ResponseUserProject[]
  >,
) => {
  return useQuery<
    ApiResponseDto<ResponseUserProject[]>,
    unknown,
    ResponseUserProject[]
  >({
    queryKey: projectQueryKeys.getUserProjectList(),
    queryFn: () => userApi.get("/project"),
    select: ({ data }) => data,
    ...options,
  });
};

export const useFetchMyProjectListInfiniteQuery = (
  params?: ProjectListSearchParamType,
  options?: Omit<
    UseInfiniteQueryOptions<
      ApiResponseDto<ResponseUserProject[]>,
      unknown,
      ResponseUserProject[]
    >,
    "select"
  >,
) => {
  return useInfiniteQuery<
    ApiResponseDto<ResponseUserProject[]>,
    unknown,
    ResponseUserProject[]
  >({
    queryKey: projectQueryKeys.getUserProjectList({
      ...(params && { params }),
    }),
    queryFn: ({ pageParam = 1 }) =>
      userApi.get("/project", {
        params: {
          ...params,
          page: pageParam,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, lastPageParam: number) => {
      return lastPage.data?.length ? lastPageParam + 1 : undefined;
    },
    select: (response) => {
      const projectList = response.pages.flatMap((page) => {
        if (Array.isArray(page.data)) {
          return page.data as ResponseUserProject[];
        }

        return [];
      });

      return projectList;
    },
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchProjectDetailQuery = (
  projectId?: string,
  options?: Omit<
    UseQueryOptions<
      ApiResponseDto<ResponseProjectDetail>,
      unknown,
      ProjectViewModel
    >,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  return useQuery<
    ApiResponseDto<ResponseProjectDetail>,
    unknown,
    ProjectViewModel
  >({
    queryKey: projectQueryKeys.getProjectDetail({ projectId }),
    queryFn: () => projectApi.get(`/${projectId}`),
    select: (response) => ProjectViewModel.create(response.data),
    enabled: !!projectId,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchProjectDetailForUpdateQuery = (
  projectId?: string,
  options?: Omit<
    UseQueryOptions<
      ApiResponseDto<ResponseProjectDetail>,
      unknown,
      ResponseProjectDetail
    >,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  return useQuery<
    ApiResponseDto<ResponseProjectDetail>,
    unknown,
    ResponseProjectDetail
  >({
    queryKey: projectQueryKeys.getProjectDetail({ projectId }),
    queryFn: () => projectApi.get(`/${projectId}`),
    select: ({ data }) => ({
      ...data,
      startDate: formatDate({
        date: ignoreTimezone(data.startDate),
        customType: "yyyy-MM-dd'T'HH:mm",
      }),
      endDate: formatDate({
        date: ignoreTimezone(data.endDate),
        customType: "yyyy-MM-dd'T'HH:mm",
      }),
    }),
    enabled: !!projectId,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchProjectScheduleQuery = (
  projectId?: string,
  options?: Omit<
    UseQueryOptions<
      ApiResponseDto<Record<string, ProjectScheduleEventType[]>>,
      unknown,
      ProjectScheduleEventType[]
    >,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  return useQuery<
    ApiResponseDto<Record<string, ProjectScheduleEventType[]>>,
    unknown,
    ProjectScheduleEventType[]
  >({
    queryKey: projectQueryKeys.getProjectSchedule({ projectId }),
    queryFn: () => projectApi.get(`/${projectId}/schedule`),
    select: ({ data }) =>
      Object.entries(data).flatMap(([_, projectEventList]) =>
        projectEventList.map((event) => ({
          ...event,
          startDate: formatDate({
            date: ignoreTimezone(event.startDate),
            customType: "yyyy-MM-dd'T'HH:mm",
          }),
        })),
      ),
    enabled: !!projectId,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchProjectStaticEventListQuery = (
  projectId?: string,
  options?: Omit<
    UseQueryOptions<
      ApiResponseDto<ProjectStaticEventType>,
      unknown,
      ProjectScheduleEventType[]
    >,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  return useQuery<
    ApiResponseDto<ProjectStaticEventType>,
    unknown,
    ProjectScheduleEventType[]
  >({
    queryKey: projectQueryKeys.getProjectStaticEventList({ projectId }),
    queryFn: () => projectApi.get(`/${projectId}/schedule/static-event`),
    select: ({ data }) =>
      data.data.map((event) => ({
        ...event,
        startDate: formatDate({
          date: ignoreTimezone(event.startDate),
          customType: "yyyy-MM-dd'T'HH:mm",
        }),
      })),
    enabled: !!projectId,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchProjectEventListQuery = (projectId?: string) => {
  return useQuery({
    queryKey: projectQueryKeys.getProjectEventList({ projectId }),
    queryFn: () =>
      projectApi.get<ApiResponseDto<ResponseProjectEvent>>(
        `/${projectId}/event`,
      ),
    select: (response) =>
      groupByList(
        response.data.list.map((data) =>
          ProjectEventInfoViewModel.create(data),
        ),
        "dateWithTime", // NOTE: dateWithTime를 기준으로 Grouping
      ),
    enabled: !!projectId,
  });
};

// ------------------------------------------------------------------------

export const useFetchProjectCommentListQuery = (projectId?: string) => {
  return useQuery({
    queryKey: projectQueryKeys.getProjectComments({ projectId }),
    queryFn: () =>
      projectApi.get<ApiResponseDto<ResponseProjectComment[]>>(
        `/${projectId}/comment`,
      ),
    select: ({ data }) => ({
      hasComment: data.length !== 0,
      commentList: data.map((res) => ProjectCommentViewModel.create(res)),
    }),
    enabled: !!projectId,
  });
};

// ------------------------------------------------------------------------

// NOTE : 에디션 참여, 취소시 채팅 이벤트 ID를 가져오기 위한 query
export const useFetchProjectEventListForChatQuery = (projectId?: string) => {
  return useQuery({
    queryKey: projectQueryKeys.getProjectEventList({ projectId }),
    queryFn: () =>
      projectApi.get<ApiResponseDto<ResponseProjectEvent>>(
        `/${projectId}/event`,
      ),
    select: (response) =>
      response.data.list.map((data) => ProjectEventInfoViewModel.create(data)),
    enabled: !!projectId,
  });
};
